import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as CheckCircle } from './icons/check_circle.svg';
import useFeedbackState, { defaultValue, FeedbackState } from './useFeedbackState';
import {
  Input,
  Textarea,
  FieldGroup,
  FieldLabel,
  SearchableDropdown,
  FEEDBACK_REGIONS,
} from '@gr-monorepo/common/public/ui';
import { Space } from '@gr-monorepo/common/ui';
import { FeedbacksApi } from 'api';
import { useAuthContext } from 'hooks/useAuthContext';
import * as S from './FeedbackStyled';
import { getFeedbackStorage, setFeedbackStorage } from './SessionStorage';
import { LogClick, useClickStreamContext } from '../ClickStream';
import { FeedbackFileUpload } from './components/FeedbackFileUpload';
import useFormState from '@gr-monorepo/common/utils/useFormState';
import { PrimaryButton } from '@gr-monorepo/common/public/ui/Buttons';
import { Api } from 'api/cabmun-content-public/models';

const FEEDBACK_REGION_OPTIONS = FEEDBACK_REGIONS.map((region) => ({
  label: region.name,
  value: region.name,
}));

const REQUIRED_FIELD_MSG = 'Поле обязательно для заполнения';

const SuccessSubmit = () => {
  return <CheckCircle />;
};

function testEmail(email: string) {
  if (/[А-Яа-я]/.test(email)) {
    return 'Русские буквы недопустимы';
  }
  if (/\s/.test(email)) {
    return 'Пробелы недопустимы';
  }
  if (!/\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/.test(email)) {
    return 'Проверьте правильность email';
  }
}

function testPhone(phone: string) {
  if (phone.replace(/[^\d]/g, '').length !== 11) {
    return 'Введите 10 цифр';
  }
}

const validateForm = (formState: FeedbackState, authorized: boolean) => {
  let isValid = true;
  if (!formState.values.fio) {
    isValid = false;
    formState.setError('fio')(REQUIRED_FIELD_MSG);
  }
  if (!formState.values.workplacePosition) {
    isValid = false;
    formState.setError('workplacePosition')(REQUIRED_FIELD_MSG);
  }
  if (!authorized && !formState.values.region.trim()) {
    isValid = false;
    formState.setError('region')(REQUIRED_FIELD_MSG);
  }
  if (!formState.values.phone && !formState.values.email) {
    isValid = false;
    formState.setError('email')(REQUIRED_FIELD_MSG);
    formState.setError('phone')(' ');
  } else {
    if (formState.values.phone) {
      const result = testPhone(formState.values.phone);
      if (result) {
        formState.setError('phone')(result || null);
        isValid = false;
      }
    }
    if (formState.values.email) {
      const result = testEmail(formState.values.email);
      if (result) {
        formState.setError('email')(result || null);
        isValid = false;
      }
    }
  }

  if (!formState.values.text) {
    isValid = false;
    formState.setError('text')(REQUIRED_FIELD_MSG);
  }
  return isValid;
};

const FeedbackForm = ({ onSuccessChange }: { onSuccessChange?: (isSuccess: boolean) => void }) => {
  const { authorized } = useAuthContext();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formState = useFeedbackState();
  const attachmentsState = useFormState<{ isSendDisabled: boolean; attachIds: number[] }>({
    isSendDisabled: false,
    attachIds: [],
  });
  const { logPageEvent } = useClickStreamContext();
  const fileUploadRef = useRef<{ removeFilesCallback: () => void }>();

  const onSubmit = async () => {
    const result = validateForm(formState, authorized);
    if (!result) return;

    const { workplacePosition, region, municipality, text, ...value } = formState.values;

    setIsSubmitting(true);
    try {
      await FeedbacksApi.createFeedback({
        ...value,
        workplace_position: workplacePosition,
        text,
        form_source: Api.CreateFeedbackRequest.FormSourceEnum.Cabmun,
        ...(authorized
          ? {}
          : {
              region: region.trim(),
              municipality: municipality.trim() || undefined,
            }),
        attachment_ids: attachmentsState.values.attachIds,
      });
      logPageEvent({
        eventType: 'feedback_sent',
        eventInfo: `Отправлена ОС`,
      });
    } catch (e) {
      logPageEvent({
        eventType: 'feedback_sent_error',
        eventInfo: `Ошибка отравки обратно связи`,
      });
      alert('Ошибка');
      return;
    } finally {
      setIsSubmitting(false);
    }

    formState.setValues({ ...defaultValue });
    fileUploadRef.current?.removeFilesCallback();
    setFeedbackStorage({ ...defaultValue });
    setIsSuccess(true);
    onSuccessChange?.(true);
  };

  const saveValues = () => {
    setFeedbackStorage(formState.values);
  };

  useEffect(() => {
    const values = getFeedbackStorage();
    if (values) formState.setValues(values);
  }, []);

  if (isSuccess)
    return (
      <S.SuccessWrap data-testid="feedback-form-success">
        <SuccessSubmit />
        <div style={{ fontSize: 24, marginTop: 24, fontWeight: 700 }}>Спасибо за обращение!</div>
      </S.SuccessWrap>
    );

  return (
    <>
      <S.Fields data-testid="feedback-form">
        <FieldGroup>
          <FieldLabel>Как вас зовут*</FieldLabel>
          <Input
            value={formState.values.fio}
            onChange={formState.setValue('fio')}
            error={!!formState.errors.fio}
            errorText={formState.errors.fio || ''}
            placeholder="Фамилия Имя Отчество"
            onBlur={saveValues}
            dataQaId="feedback-form-fio"
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Кем вы работаете*</FieldLabel>
          <Input
            value={formState.values.workplacePosition}
            onChange={formState.setValue('workplacePosition')}
            error={!!formState.errors.workplacePosition}
            errorText={formState.errors.workplacePosition || ''}
            placeholder="Ваша должность"
            onBlur={saveValues}
            dataQaId="feedback-form-workplace-position"
          />
        </FieldGroup>

        {!authorized && (
          <>
            <FieldGroup>
              <FieldLabel>Регион*</FieldLabel>
              <SearchableDropdown
                value={formState.values.region}
                onChange={formState.setValue('region')}
                options={FEEDBACK_REGION_OPTIONS}
                onBlur={saveValues}
                error={!!formState.errors.region}
                errorText={formState.errors.region || ''}
                placeholder="Выберите из списка"
                dataTestId="feedback-form-region"
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Муниципалитет</FieldLabel>
              <Input
                value={formState.values.municipality}
                onChange={formState.setValue('municipality')}
                error={!!formState.errors.municipality}
                errorText={formState.errors.municipality || ''}
                placeholder="Название"
                onBlur={saveValues}
                dataQaId="feedback-form-municipality"
              />
            </FieldGroup>
          </>
        )}

        <FieldGroup>
          <FieldLabel>Как с вами связаться*</FieldLabel>
          <S.FieldFlexRow>
            <Input
              value={formState.values.email}
              onChange={formState.setValue('email')}
              error={!!formState.errors.email}
              errorText={formState.errors.email || ''}
              placeholder="email@pochta.ru"
              style={{ width: '100%' }}
              onBlur={saveValues}
              dataQaId="feedback-form-email"
            />
            <Space size={16} />
            или
            <Space size={16} />
            <Input
              value={formState.values.phone}
              onChange={formState.setValue('phone')}
              error={!!formState.errors.phone}
              errorText={formState.errors.phone || ''}
              placeholder="+7 (XXX) XXX-XX-XX"
              style={{ width: '100%' }}
              mask="+7 (000) 000-00-00"
              onBlur={saveValues}
              dataQaId="feedback-form-phone"
            />
          </S.FieldFlexRow>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Ваш вопрос или предложение*</FieldLabel>
          <Textarea
            value={formState.values.text}
            onChange={formState.setValue('text')}
            error={!!formState.errors.text}
            errorText={formState.errors.text || ''}
            placeholder="Напишите обращение"
            onBlur={saveValues}
            dataTestId="feedback-form-text"
          />
        </FieldGroup>
      </S.Fields>

      <Space size={20} axis="vertical" />
      <S.HintText>Поля, помеченные *, обязательны для заполнения</S.HintText>
      <Space size={20} axis="vertical" />
      <FeedbackFileUpload
        isErrorOrLoading={attachmentsState.setValue('isSendDisabled')}
        onListUpdate={attachmentsState.setValue('attachIds')}
        ref={fileUploadRef}
      />
      <Space size={16} axis="vertical" />
      <PrimaryButton
        type="button"
        size="xlarge"
        fullWidth
        onClick={onSubmit}
        loading={isSubmitting}
        disabled={attachmentsState.values.isSendDisabled}
        data-testid="feedback-form-submit"
      >
        Отправить
      </PrimaryButton>
      <Space size={16} axis="vertical" />
      <S.PrivacyText>
        Нажимая «Отправить», вы соглашаетесь с{' '}
        <LogClick eventType="feedback_open_privacy" eventInfo={`Нажал открыть политику обработки ПД в обратной связи`}>
          <S.PolicyLink href="https://uchi.ru/privacy" target="_blank">
            политикой обработки персональных данных
          </S.PolicyLink>
        </LogClick>
      </S.PrivacyText>
    </>
  );
};

export default FeedbackForm;
